// @givmo/board — what a person's to-do list holds, item by item (CBG-UX7-WEB-DIR, CBG-UX7-WEB-OFF;
// design stop design-268's §4 and §6).
//
// ONE shape for both lists. Each item says what to do (`line`), which one (`detail`), by when where
// the record has a date (`due`), and where it is done (`target`), which is the page that already does
// it: the list adds no act of its own but a step's "Mark done", the member's own list's act, and the
// retired card's Open on minutes waiting while the signing window is shut (CBG-FIX74). Every item
// carries the identities the store's "something new opens it once" rule tracks (`todo-store.tsx`).
//
// Every read is decoded field by field off `unknown`, the package's tolerant reader: the generated
// unions are a compile-time claim, a malformed entry is left out, and an unknown kind is a newer
// backend. The director's list is composed beside the director's pages
// (`features/director/director-todo-list.ts`), where the capacity rail keeps director reads; this
// module holds the shape, the readers both use, and the officer's list.
//
// ══ THE OFFICER'S LIST: ONE READ (§6.2) ═══════════════════════════════════════════════════════════
//
//   `GET …/to-do`, in the backend's order (a new member's steps; the minutes by their Form 990 date;
//   records to approve; proposals), each kind composed only for a holder of its act's string, so an
//   officer is never listed what they can't do. Never what a member hasn't read or answered: that is
//   theirs (plan §1.7, "measurement, not shaming").

import { BOARD_PAGES } from '../../board-routes';
import { shortDay } from '../conflicts/coi-decode';
import { meetingDayOf } from '../in-meeting/in-meeting-decode';
import { theClosedMinutesOf } from '../minutes/minutes-rule-words';
import { todoWords as w } from './todo-words';

/** Where an item is done: a package-relative address, with its params and search. */
export interface ToDoTarget {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
}

export interface ToDoItem {
  /** Stable within the list: the row's React key and its testid's tail. */
  key: string;
  /** What the store remembers as shown: each underlying thing to do (a consents row has several). */
  identities: string[];
  group: 'checklist' | 'waiting';
  line: string;
  /** Which one: the meeting, the person, the record. */
  detail?: string | null;
  /** By when, where the record has a date: its own quiet line. */
  due?: string | null;
  /** A real link to where it is done. */
  target?: ToDoTarget;
  /**
   * Instead of a link, the row's one button: a step the member marks themselves ("Mark done"), or
   * CBG-FIX74's Open, which brings the confirmation up by itself.
   */
  act?: { kind: 'mark-done'; itemId: string } | { kind: 'open-waiting-minutes' };
  /** What it adds to the count (a row for several written consents counts each one). */
  count: number;
  /** Its due line is the Form 990 date (the list then says what that date is, once). */
  form990?: boolean;
  /** A committee's minutes: their Form 990 date counts from the committee's next meeting, so the
   *  list then says that date's rule in its committee form (`form990-date-words.ts`). */
  committee?: boolean;
}

export interface DoneItem {
  key: string;
  /** What was done, in the past tense of its to-do. */
  line: string;
  /** Which one: the meeting, the person (as the to-do's own detail). */
  which?: string | null;
  /** When, and by whom when it wasn't the reader. */
  detail: string;
  /** The instant (or day) it was done: newest first. */
  at: string;
  target?: ToDoTarget;
}

export interface ToDoList {
  items: ToDoItem[];
  completed: DoneItem[];
}

/** The number on the tab and beside "To do". */
export function countOf(items: readonly ToDoItem[]): number {
  return items.reduce((sum, item) => sum + item.count, 0);
}

/** Every identity the list holds (the store's "seen"). */
export function identitiesOf(list: ToDoList): string[] {
  return list.items.flatMap((item) => item.identities);
}

/** Completed's window and cap, the backend's (§6.1): the last 90 days, at most 50. */
export const COMPLETED_LIMIT = 50;
export const COMPLETED_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

// ── Reading off `unknown` ─────────────────────────────────────────────────────────────────────────

export type Entry = Record<string, unknown>;

export function isEntry(value: unknown): value is Entry {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

export function entriesOf(value: unknown): Entry[] {
  return Array.isArray(value) ? value.filter(isEntry) : [];
}

/** An instant or a calendar day, as milliseconds; null when it can't be read. */
export function timeOf(value: string): number | null {
  const parsed = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Newest first, the order a tie keeps stable; at most `COMPLETED_LIMIT`. */
export function newestFirst(items: readonly DoneItem[]): DoneItem[] {
  return [...items]
    .sort(
      (left, right) =>
        (timeOf(right.at) ?? 0) - (timeOf(left.at) ?? 0) || (left.key < right.key ? -1 : 1),
    )
    .slice(0, COMPLETED_LIMIT);
}

// ── Dates, as the list says them ──────────────────────────────────────────────────────────────────

/** A meeting's day on its own clock, "2027-10-12"; null when its start or zone can't be read. */
export function meetingDay(startsAt: string | null, zone: string | null): string | null {
  return meetingDayOf({ starts_at: startsAt, time_zone: zone })?.day ?? null;
}

/** When it was done, and by whom when the record names someone else (§4.4). */
function doneLine(when: string, byYou: boolean, name: string | null): string {
  return byYou || name === null ? when : w.doneBy(when, name);
}

// ── The officer's list ────────────────────────────────────────────────────────────────────────────

/** The minutes' "which one": the meeting and its day, or a closed session's minutes by their public
 *  facts (RC11-COPY2's approved forms); a committee's meeting says its committee first. */
function minutesWhich(entry: Entry): string {
  const title = text(entry.meeting_title);
  const day = meetingDay(text(entry.starts_at), text(entry.time_zone));
  const committee = text(entry.committee_name);
  const which =
    text(entry.segment_id) !== null
      ? theClosedMinutesOf(title, day, null, { keepDateTogether: true })
      : w.meetingAndDate(title ?? '', day === null ? '' : shortDay(day));
  return committee === null ? which : `${committee} · ${which}`;
}

/** Where a meeting's minutes are written and sent: its Minutes tab, on a closed session's own. */
function minutesTarget(entry: Entry, meetingId: string): ToDoTarget {
  const segmentId = text(entry.segment_id);
  return {
    to: '/meetings/$meetingId/minutes',
    params: { meetingId },
    ...(segmentId === null ? {} : { search: { session: segmentId } }),
  };
}

/** The officer's read (`GET …/to-do`), item by item in the backend's order, and its Completed. */
export function officerList(body: unknown, orgRef: string): ToDoList {
  if (!isEntry(body)) return { items: [], completed: [] };
  const id = (identity: string) => `${orgRef}:${identity}`;
  const group = 'waiting' as const;
  const items: ToDoItem[] = [];
  for (const entry of entriesOf(body.items)) {
    switch (entry.kind) {
      case 'new_member_step': {
        const memberId = text(entry.board_member_id);
        const name = text(entry.display_name);
        if (memberId === null || name === null) continue;
        const joinedOn = text(entry.joined_on);
        const detail = w.newMember(name, joinedOn === null ? null : shortDay(joinedOn));
        const target = { to: '/roster/$memberId', params: { memberId } };
        if (entry.step === 'send_questionnaire') {
          items.push({
            key: `questionnaire-${memberId}`,
            identities: [id(`questionnaire:${memberId}:${text(entry.cycle_id) ?? ''}`)],
            group,
            line: w.sendQuestionnaire(text(entry.label) ?? ''),
            detail,
            target,
            count: 1,
          });
        } else if (entry.step === 'choose_buddy') {
          items.push({
            key: `buddy-${memberId}`,
            identities: [id(`buddy:${memberId}`)],
            group,
            line: w.chooseBuddy,
            detail,
            target,
            count: 1,
          });
        }
        break;
      }
      case 'minutes_to_write':
      case 'minutes_to_send': {
        const meetingId = text(entry.meeting_id);
        if (meetingId === null) continue;
        const scope = text(entry.segment_id) ?? 'meeting';
        const form990 = text(entry.form_990_date);
        items.push({
          key: `${entry.kind}-${meetingId}-${scope}`,
          identities: [id(`${entry.kind}:${meetingId}:${scope}`)],
          group,
          line: entry.kind === 'minutes_to_write' ? w.writeMinutes : w.sendMinutes,
          detail: minutesWhich(entry),
          due: form990 === null ? null : w.form990Date(shortDay(form990)),
          form990: form990 !== null,
          committee: text(entry.committee_name) !== null,
          target: minutesTarget(entry, meetingId),
          count: 1,
        });
        break;
      }
      case 'record_to_approve': {
        const recordId = text(entry.record_id);
        if (recordId === null) continue;
        items.push({
          key: `approve-${recordId}`,
          identities: [id(`approve:${recordId}`)],
          group,
          line: w.approveRecord,
          detail: text(entry.title),
          target: { to: '/records/$recordId', params: { recordId } },
          count: 1,
        });
        break;
      }
      // EMC-WEB4 (frame 22): a Board Ask waiting for approval, "Approve the <campaign> Board Ask",
      // from whoever drafted it.
      case 'board_ask_to_approve': {
        const askId = text(entry.ask_id);
        const campaign = text(entry.campaign_name);
        if (askId === null || campaign === null) continue;
        const drafter = text(entry.drafted_by_display_name);
        items.push({
          key: `board-ask-${askId}`,
          identities: [id(`board-ask:${askId}`)],
          group,
          line: w.approveBoardAsk(campaign),
          detail: drafter === null ? null : w.boardAskFrom(drafter),
          target: { to: '/asks/$askId', params: { askId } },
          count: 1,
        });
        break;
      }
      case 'proposed_members': {
        const count = typeof entry.count === 'number' ? entry.count : 0;
        const proposedAt = text(entry.proposed_at);
        if (count < 1 || proposedAt === null) continue;
        items.push({
          key: 'proposed-members',
          // The newest proposal's instant: another proposal is something new.
          identities: [id(`proposed-members:${proposedAt}`)],
          group,
          line: w.reviewMembers(count),
          detail: w.proposedBy(shortDay(proposedAt)),
          target: { to: BOARD_PAGES.members },
          count: 1,
        });
        break;
      }
      case 'past_records': {
        const batchId = text(entry.batch_id);
        const count = typeof entry.count === 'number' ? entry.count : 0;
        const proposedAt = text(entry.proposed_at);
        if (batchId === null || count < 1 || proposedAt === null) continue;
        items.push({
          key: `past-records-${batchId}`,
          identities: [id(`past-records:${batchId}`)],
          group,
          line: w.reviewRecords(count),
          detail: w.proposedBy(shortDay(proposedAt), count === 1),
          target: { to: BOARD_PAGES.proposedBatch, params: { batchId } },
          count: 1,
        });
        break;
      }
      default:
        break;
    }
  }
  return { items, completed: newestFirst(officerCompleted(body.completed)) };
}

function officerCompleted(value: unknown): DoneItem[] {
  const done: DoneItem[] = [];
  for (const entry of entriesOf(value)) {
    const at = text(entry.done_at);
    if (at === null) continue;
    const detail = doneLine(
      w.doneOn(shortDay(at)),
      entry.done_by_you === true,
      text(entry.done_by_display_name),
    );
    switch (entry.kind) {
      case 'minutes_sent': {
        const meetingId = text(entry.meeting_id);
        if (meetingId === null) continue;
        done.push({
          key: `sent-${meetingId}-${text(entry.segment_id) ?? 'meeting'}-${at}`,
          line: w.sentMinutes,
          which: minutesWhich(entry),
          detail,
          at,
          target: minutesTarget(entry, meetingId),
        });
        break;
      }
      case 'record_approved': {
        const recordId = text(entry.record_id);
        if (recordId === null) continue;
        done.push({
          key: `approved-${recordId}`,
          line: w.approvedRecord,
          which: text(entry.title),
          detail,
          at,
          target: { to: '/records/$recordId', params: { recordId } },
        });
        break;
      }
      case 'member_proposal_added': {
        const memberId = text(entry.board_member_id);
        if (memberId === null) continue;
        done.push({
          key: `added-${memberId}`,
          line: w.addedProposedMember,
          which: text(entry.display_name),
          detail,
          at,
          target: { to: '/roster/$memberId', params: { memberId } },
        });
        break;
      }
      case 'member_proposal_discarded':
        // Never named: a discard clears the proposal's name.
        done.push({
          key: `discarded-${at}`,
          line: w.discardedProposedMember,
          detail,
          at,
          target: { to: BOARD_PAGES.members },
        });
        break;
      case 'past_records_imported': {
        const batchId = text(entry.batch_id);
        const count = typeof entry.count === 'number' ? entry.count : 0;
        if (batchId === null || count < 1) continue;
        done.push({
          key: `imported-${batchId}`,
          line: w.importedRecords(count),
          which: w.proposedThem(count === 1),
          detail,
          at,
          // CBG-UX11 (design-284 §2.5): the import page, which lists what came in; no batch's page.
          target: { to: BOARD_PAGES.importBatches },
        });
        break;
      }
      case 'buddy_chosen': {
        const memberId = text(entry.board_member_id);
        if (memberId === null) continue;
        done.push({
          // A buddy changed is a second choice for the same member: keyed by its instant too.
          key: `buddy-${memberId}-${at}`,
          line: w.choseBuddy,
          which: text(entry.display_name),
          detail,
          at,
          target: { to: '/roster/$memberId', params: { memberId } },
        });
        break;
      }
      case 'board_ask_sent': {
        const askId = text(entry.ask_id);
        if (askId === null) continue;
        done.push({
          key: `board-ask-sent-${askId}`,
          line: w.sentBoardAsk,
          which: text(entry.campaign_name),
          detail,
          at,
          target: { to: '/asks/$askId', params: { askId } },
        });
        break;
      }
      case 'questionnaire_sent': {
        const memberId = text(entry.board_member_id);
        if (memberId === null) continue;
        done.push({
          key: `questionnaire-${memberId}-${text(entry.cycle_id) ?? ''}`,
          line: w.sentQuestionnaire(text(entry.label) ?? ''),
          which: text(entry.display_name),
          detail,
          at,
          target: { to: '/roster/$memberId', params: { memberId } },
        });
        break;
      }
      default:
        break;
    }
  }
  return done;
}
